import { createAsyncThunk } from "@reduxjs/toolkit";
import { OrderAndSalesAxios } from "../../api/orderandsalesaxios";
import { FilterBuilder } from "../../helpers/ThunkHelper/FilterBuilder";
import { API_ENDPOINT } from "../../helpers/Endpoints/endpoints";
import { Axios } from "api/axios";

// ✅ Utility to build query params for list API
const buildQueryParams = (
  page: number,
  pageSize: number,
  // 💡 Note the order: type is 3rd
  type: string | any,
  search: string | any,
  sort: string | any,
  order: "asc" | "desc" | "" | any,
  filters: any
) => {
  const params = new URLSearchParams();
  params.append("page", page.toString());
  params.append("page_size", pageSize.toString());
  params.append("enquiry_stage", type.toString());
  if (search) params.append("search", search);

  if (sort) {
    const sortMap: any = {
      company_name: "company__company_name",
      agent_name: "agent_agent_name",
      item_name: "items__item__item_name",
    };

    const finalSortField = sortMap[sort] || sort;

    const orderingValue =
      order === "desc" ? `-${finalSortField}` : finalSortField;

    params.append("ordering", orderingValue);
  }

  // ✅ Defensive fix — ensure filters is always an array
  const safeFilters = Array.isArray(filters) ? filters : [];

  if (safeFilters.length > 0) {
    safeFilters.forEach(
      ({
        filterField,
        filterValue,
      }: {
        filterField: string;
        filterValue: any;
      }) => {
        if (filterField && filterValue !== undefined && filterValue !== null) {
          //  HANDLE DATE RANGE FILTERS FIRST
          if (
            filterField === "created_at" ||
            filterField === "updated_at" ||
            filterField === "enquiry_date" ||
            filterField === "received_date"
          ) {
            const from = filterValue?.from || filterValue?.from_date;
            const to = filterValue?.to || filterValue?.to_date;

            if (from) params.append(`${filterField}_from`, from);
            if (to) params.append(`${filterField}_to`, to);

            return; //  stop processing this filter here
          }

          //  GENERIC OBJECT FILTER (dropdowns, etc.)
          if (typeof filterValue === "object" && filterValue !== null) {
            Object.entries(filterValue).forEach(([key, value]) => {
              if (value !== undefined && value !== null && value !== "") {
                params.append(key, String(value));
              }
            });
          } else {
            //  SIMPLE FILTERS
            params.append(filterField, filterValue);
          }
        }
      }
    );
  }

  return params;
};

// ----------------------------------------------------
// ✅ PRODUCT ENQUIRY THUNKS
// ----------------------------------------------------

export const fetchProductEnquiries = createAsyncThunk(
  "enquiry/fetchProductEnquiries",
  async ({
    page,
    pageSize,
    sort,
    order,
    search,
    type,
    filters,
    stage,
  }: {
    page: number;
    pageSize: number;
    search?: string;
    type?: string;
    sort?: string;
    order?: "asc" | "desc" | "";
    filters?: any[];
    stage?: string;
  }) => {
    try {
      const params = new URLSearchParams();
      params.append("page", page.toString());
      params.append("page_size", pageSize.toString());
      // params.append("stage", stage || "");

      if (search) params.append("search", search);
      if (type) params.append("type", type);
      if (sort) {
        const orderingValue = order === "desc" ? `-${sort}` : sort;
        params.append("ordering", orderingValue);
      }

      const filterPayload = FilterBuilder(filters ?? []);

      if (!filterPayload.enquiry_stage) {
        filterPayload.enquiry_stage = stage?.toLowerCase(); // "enquiry_log" or "draft"
      }

      const body = {
        filters: filterPayload,
        ...(type ? { type } : {}),
      };

      const response: any = await Axios.post(
        `${API_ENDPOINT?.ENQUIRY?.PRODUCT?.LIST}?${params.toString()}`,
        body
      );

      console.log("response from thunk for product", response.data)
      return {
        data: response.data.data || [],
        rowCount: response.data.totalRecords || 0,
        todaysFollowUpCount: response.data.todaysFollowUpCount || 0,
      };
    } catch (error: any) {
      throw new Error("Failed to fetch data");
    }
  }
);

export const fetchProductEnquiryById = createAsyncThunk(
  "enquiry/fetchProductEnquiryById",
  async (id: string, { rejectWithValue }) => {
    try {
      const response: any = await Axios.get(
        `${API_ENDPOINT?.ENQUIRY?.PRODUCT?.DETAIL(id)}`
      );

      return response.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "Failed to fetch Product Enquiry by ID"
      );
    }
  }
);

export const createProductEnquiry = createAsyncThunk(
  "enquiry/createProductEnquiry",
  async (formData: FormData, { rejectWithValue }) => {
    try {
      const response: any = await Axios.post(
        `${API_ENDPOINT?.ENQUIRY?.PRODUCT?.CREATE}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return response.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "Failed to create Product Enquiry"
      );
    }
  }
);

export const updateProductEnquiry = createAsyncThunk(
  "enquiry/updateProductEnquiry",
  async (
    { id, formData }: { id: string; formData: FormData },
    { rejectWithValue }
  ) => {
    try {
      const response: any = await Axios.put(
        `${API_ENDPOINT?.ENQUIRY?.PRODUCT?.UPDATE(id)}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return response.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "Failed to update Product Enquiry"
      );
    }
  }
);

export const deleteProductEnquiry = createAsyncThunk(
  "enquiry/deleteProductEnquiry",
  async (id: string, { rejectWithValue }) => {
    try {
      await Axios.delete(
        `${API_ENDPOINT?.ENQUIRY?.PRODUCT?.DELETE(id)}`
      );
      return id;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "Failed to delete Product Enquiry"
      );
    }
  }
);

export const toggleProductEnquiryStatus = createAsyncThunk(
  "enquiry/toggleProductEnquiryStatus",
  async ({ id, is_active }: { id: string; is_active: boolean }) => {
    const payload = { is_active };
    const response: any = await Axios.patch(
      `${API_ENDPOINT?.ENQUIRY?.PRODUCT?.STATUS_UPDATE(id)}`,
      payload
    );
    return response.data.data;
  }
);

export const fetchProductEnquiryDropdown = createAsyncThunk(
  "enquiry/fetchProductEnquiryDropdown",
  async (type: string, { rejectWithValue }) => {
    try {
      const response: any = await Axios.get(
        `/enquiry/product/dropdown/?name=${type}`
      );
      return response.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "Failed to fetch Product Enquiry dropdown"
      );
    }
  }
);

export const fetchProductEnquiryDynamicDropdown = createAsyncThunk(
  "enquiry/fetchProductEnquiryDynamicDropdown",
  async (field_name: string, { rejectWithValue }) => {
    try {
      const response: any = await Axios.get(
        `/enquiry/product/dynamic_dropdown/${field_name}/`
      );
      return response.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "Failed to fetch Product Enquiry dynamic dropdown"
      );
    }
  }
);

export const handleProductEnquiryStatus = createAsyncThunk(
  "enquiry/handleProductEnquiryStatus",
  async ({
    id,
    enquiry_stage,
    status,
    remark
  }: {
    id: string;
    enquiry_stage: string;
    status?: string;
    remark?:string;
  }) => {
    const payload = {
      id: id,
      enquiry_stage: enquiry_stage,
      status: status || null,
      remark: remark || null
    };

    const response: any = await Axios.patch(
      `${API_ENDPOINT?.ENQUIRY?.PRODUCT?.STATUS_UPDATE(id)}`,
      payload
    );
    return response.data;
  }
);

// ----------------------------------------------------
// ✅ SERVICE ENQUIRY THUNKS
// ----------------------------------------------------

export const fetchServiceEnquiries = createAsyncThunk(
  "enquiry/fetchServiceEnquiries",
  async ({
    page,
    pageSize,
    sort,
    order,
    search,
    type,
    filters,
    stage,
  }: {
    page: number;
    pageSize: number;
    search?: string;
    type?: string;
    sort?: string;
    order?: "asc" | "desc" | "";
    filters?: any;
    stage?: string;
  }) => {
    const params = new URLSearchParams();
    params.append("page", page.toString());
    params.append("page_size", pageSize.toString());
    params.append("stage", stage || "");
    if (search) params.append("search", search);
    if (type) params.append("type", type);
    if (sort) {
      const orderingValue = order === "desc" ? `-${sort}` : sort;
      params.append("ordering", orderingValue);
    }

    const filterPayload = FilterBuilder(filters ?? []);

    if (!filterPayload.enquiry_stage) {
      filterPayload.enquiry_stage = stage?.toLowerCase(); // "enquiry_log" or "draft"
    }

    const body = {
      filters: filterPayload,
      ...(type ? { type } : {}),
    };

    const response: any = await Axios.post(
      `${API_ENDPOINT?.ENQUIRY.SERVICE.LIST}?${params.toString()}`,
      body
    );
    return {
      data: response.data.data || [],
      rowCount: response.data.totalRecords || 0,
      todaysFollowUpCount: response.data.todaysFollowUpCount || 0,
    };
  }
);

export const fetchServiceEnquiryById = createAsyncThunk(
  "enquiry/fetchServiceEnquiryById",
  async (id: any, { rejectWithValue }) => {
    try {
      const response: any = await Axios.get(
        `${API_ENDPOINT?.ENQUIRY?.SERVICE?.DETAIL(id)}`
      );

      return response.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "Failed to fetch Service Enquiry by ID"
      );
    }
  }
);

export const createServiceEnquiry = createAsyncThunk(
  "enquiry/createServiceEnquiry",
  async (formData: FormData, { rejectWithValue }) => {
    try {
      const response: any = await Axios.post(
        `${API_ENDPOINT?.ENQUIRY?.SERVICE?.CREATE}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return response.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "Failed to create Service Enquiry"
      );
    }
  }
);

export const updateServiceEnquiry = createAsyncThunk(
  "enquiry/updateServiceEnquiry",
  async (
    { id, formData }: { id: string; formData: FormData },
    { rejectWithValue }
  ) => {
    try {
      const response: any = await Axios.put(
        `${API_ENDPOINT?.ENQUIRY?.SERVICE?.UPDATE(id)}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return response.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "Failed to update Service Enquiry"
      );
    }
  }
);

export const deleteServiceEnquiry = createAsyncThunk(
  "enquiry/deleteServiceEnquiry",
  async (id: string, { rejectWithValue }) => {
    try {
      await Axios.delete(`${API_ENDPOINT?.ENQUIRY?.SERVICE?.DELETE(id)}`);
      return id;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "Failed to delete Service Enquiry"
      );
    }
  }
);

// unused
export const toggleServiceEnquiryStatus = createAsyncThunk(
  "enquiry/toggleServiceEnquiryStatus",
  async ({ id, status }: { id: string; status: string }) => {
    const payload = { status };
    const response: any = await Axios.patch(
      `${API_ENDPOINT?.ENQUIRY?.SERVICE?.STATUS_UPDATE(id)}`,
      payload
    );
    return response.data;
  }
);

export const handleServiceEnquiryStatus = createAsyncThunk(
  "enquiry/handleServiceEnquiryStatus",
  async ({
    id,
    enquiry_stage,
    status,
    remark
  }: {
    id: string;
    enquiry_stage: string;
    status?: string;
    remark?: string;
  }) => {
    const payload = {
      id: id,
      enquiry_stage: enquiry_stage,
      status: status || null,
      remark: remark || null
    };
    const response: any = await Axios.patch(
      `${API_ENDPOINT?.ENQUIRY?.SERVICE?.STATUS_UPDATE(id)}`,
      payload
    );
    return response.data;
  }
);

export const fetchServiceEnquiryDropdown = createAsyncThunk(
  "enquiry/fetchServiceEnquiryDropdown",
  async (type: any, { rejectWithValue }) => {
    try {
      const response: any = await Axios.get(
        `${API_ENDPOINT?.ENQUIRY?.SERVICE?.DROPDOWN}`
      );
      return response.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "Failed to fetch Service Enquiry dropdown"
      );
    }
  }
);

export const fetchServiceEnquiryDynamicDropdown = createAsyncThunk(
  "enquiry/fetchServiceEnquiryDynamicDropdown",
  async (field_name: string, { rejectWithValue }) => {
    try {
      const response: any = await Axios.get(
        `${API_ENDPOINT?.ENQUIRY?.SERVICE?.FILTER_DROPDOWN(field_name)}`
      );
      return response.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "Failed to fetch Service Enquiry dynamic dropdown"
      );
    }
  }
);

export const fetchProductEnquiryOrderDropdown = createAsyncThunk(
  "enquiry/fetchProductEnquiryOrderDropdown",
  async (_, { rejectWithValue }) => {
    try {
      const response: any = await Axios.get(
        `/order/enquiry/product/dropdown/`
      );
      return response.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "Failed to fetch Product Enquiry Order dropdown"
      );
    }
  }
);

export const fetchServiceEnquiryOrderDropdown = createAsyncThunk(
  "enquiry/fetchServiceEnquiryOrderDropdown",
  async (_, { rejectWithValue }) => {
    try {
      const response: any = await Axios.get(
        `/order/enquiry/service/dropdown/`
      );
      return response.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "Failed to fetch Service Enquiry Order dropdown"
      );
    }
  }
);

//followUps

export const createEnquiryFollowUp = createAsyncThunk(
  "enquiry/fetchEnquiryFollowUpCreate",
  async (formData:FormData, { rejectWithValue }) => {
    try {
      const response: any = await Axios.post(
        `${API_ENDPOINT?.ENQUIRY?.FOLLOW_UP?.CREATE}`,
        formData
      );

      return response.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "Failed to create the follow Up"
      );
    }
  }
);

export const fetchFollowUpsByType = createAsyncThunk(
  "enquiry/fetchFollowUpsByTypeId",
  async (
    {
      source_entity_type,
      source_entity_id,
      source_category
    }: {
      source_entity_type: string;
      source_entity_id: string;
      source_category?: string;
    },
    { rejectWithValue }
  ) => {
    try {

      const response = await Axios.get(
        API_ENDPOINT?.ENQUIRY?.FOLLOW_UP?.DETAIL,
        {
          params: {
            source_entity_type,
            source_entity_id,
           ...(source_category && { source_category }),
          },
        }
      );

      return response.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "Failed to fetch Follow Up"
      );
    }
  }
);


// ----------------------------------------------------
// ✅ CATEGORY, SUBCATEGORY, MAKE, MODEL THUNKS
// ----------------------------------------------------

// Fetch all categories
export const fetchCategories = createAsyncThunk(
  "enquiry/fetchCategories",
  async (itemtype: any, { rejectWithValue }) => {
    try {
      const response: any = await Axios.get(
        `/enquiry/categories/?item_type=${itemtype}`
      );
      return response.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "Failed to fetch categories"
      );
    }
  }
);

// Fetch subcategories by category ID
interface FetchSubcategoriesArgs {
  item_type: "products" | "services" | string; // Use the actual types
  categoryId: string;
}

export const fetchSubcategories = createAsyncThunk(
  "enquiry/fetchSubcategories",
  async (
    { item_type, categoryId }: FetchSubcategoriesArgs,
    { rejectWithValue }
  ) => {
    try {
      const response: any = await Axios.get(
        `/enquiry/subcategories/${categoryId}/?item_type=${item_type}`
      );

      return response.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "Failed to fetch subcategories"
      );
    }
  }
);

// Fetch makes by category + subcategory
export const fetchMakes = createAsyncThunk(
  "enquiry/fetchMakes",
  async (
    {
      categoryId,
      subcategoryId,
    }: { categoryId: string; subcategoryId: string },
    { rejectWithValue }
  ) => {
    try {
      const response: any = await Axios.get(
        `/enquiry/makes/${categoryId}/${subcategoryId}/`
      );
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || "Failed to fetch makes");
    }
  }
);

// Fetch models by category + subcategory + make
export const fetchModels = createAsyncThunk(
  "enquiry/fetchModels",
  async (
    {
      categoryId,
      subcategoryId,
      makeId,
    }: { categoryId: string; subcategoryId: string; makeId: string },
    { rejectWithValue }
  ) => {
    try {
      const response: any = await Axios.get(
        `/enquiry/models/${categoryId}/${subcategoryId}/${makeId}/`
      );
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || "Failed to fetch models");
    }
  }
);

// ----------------------------------------------------
// ✅ EXPORT
// ----------------------------------------------------

export const exportEnquiryCSV = async ({
  search,
  filters,
  type, // "product" | "service"
  stage, // enquiry_log | draft
}: {
  search?: string;
  filters?: any;
  type: "product" | "service";
  stage?: string;
}) => {
  const params = new URLSearchParams();
  if (stage) params.append("stage", stage);
  if (search) params.append("search", search);
  const filterPayload = FilterBuilder(filters ?? []);

  if (!filterPayload.enquiry_stage) {
    filterPayload.enquiry_stage = stage?.toLowerCase(); // "enquiry_log" or "draft"
  }

  const body = {
    filters: filterPayload,
  };

  const response = await Axios.post(
    `/order/enquiry/${type}/export-csv?${params.toString()}`,
    body,
    {
      responseType: "blob",
    }
  );

  return {
    blob: response.data,
    filename:
      response.headers["content-disposition"]
        ?.split("filename=")[1]
        ?.replace(/"/g, "") || `${type}-enquiry.csv`,
  };
};
